import type { ApprovalAttributes } from '@/types/approval';

/** SA 신규 출장비 정산 신청 — 레거시 appr_edit_forms_eas_new_travel */
export const SA_TRAVEL_EXPENSE_TYPE = 177;

export const isSaTravelExpenseType = (apvType: number): boolean =>
  apvType === SA_TRAVEL_EXPENSE_TYPE;

/** SA 신규 출장비 사전 신청 — 레거시 appr_edit_forms_eas_confirm_travel. 진입·저장·SAP 상태 전송이 177 과 같음 */
export const SA_TRAVEL_REQUEST_TYPE = 179;

export const isSaTravelRequestType = (apvType: number): boolean =>
  apvType === SA_TRAVEL_REQUEST_TYPE;

/** 177·179 공통 — 진입 쿼리·임시저장 없음·편집 숨김 */
export const isSaTravelType = (apvType: number): boolean =>
  isSaTravelExpenseType(apvType) || isSaTravelRequestType(apvType);

/** 외부 시스템 → 작성 화면 진입 쿼리 키 — 레거시 `/intranet/approval/create?apv_type=177&sa_docno=&sa_ztype=` 와 동일 */
export const SA_TRAVEL_DOC_NO_QUERY_KEY = 'sa_docno';
export const SA_TRAVEL_Z_TYPE_QUERY_KEY = 'sa_ztype';

/** draft-source·저장 attributes·SAP 상태 전송 모두 base64 — SAP 가 base64 문서번호로만 조회됨(스웨거의 '평문' 설명과 다름) */
export type SaTravelEntry = {
  documentNumber: string;
  zType: string;
};

/** 레거시는 받은 값을 그대로 SAP 에 넘김. 숫자만 오면 평문이라 base64 로 맞춤 */
export const toSaTravelBase64 = (raw: string): string =>
  /^\d+$/.test(raw) ? btoa(raw) : raw;

const pickQuery = (
  query: Record<string, string | string[] | undefined>,
  key: string,
): string | undefined => {
  const raw = query[key];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export const getEntrySaTravel = (
  query: Record<string, string | string[] | undefined>,
): SaTravelEntry | undefined => {
  const documentNumber = pickQuery(query, SA_TRAVEL_DOC_NO_QUERY_KEY);
  const zType = pickQuery(query, SA_TRAVEL_Z_TYPE_QUERY_KEY);
  if (!documentNumber || !zType) return undefined;
  return {
    documentNumber: toSaTravelBase64(documentNumber),
    zType: toSaTravelBase64(zType),
  };
};

export type SaTravelItem = {
  Seq?: string;
  Blart?: string;
  Budat?: string;
  Belnr?: string;
  Waers?: string;
  Wrbtr?: string;
  Dmbtr?: string;
  Bktxt?: string;
  Text1?: string;
  Text2?: string;
  Text3?: string;
  Urllink2?: string;
  Zsum?: string;
};

/** 179 출장자별 행(TravelItem2) */
export type SaTravelPersonItem = {
  PernrNo?: string;
  Pernr?: string;
  DatumFr?: string;
  DatumTo?: string;
  Trdays?: string;
  CityFr?: string;
  CityTo?: string;
  StateTo?: string;
  Airplain?: string;
  Seat?: string;
  Hotel?: string;
  Exp01?: string;
  Exp02?: string;
  Exp03?: string;
  Exp04?: string;
  Exp05?: string;
  Etc?: string;
  Zpay?: string;
  Zuse?: string;
  Deduc?: string;
};

/** SAP TravelHeaderSet 한 건 — 화면에 쓰는 키만 */
export type SaTravelHeader = {
  Zuse?: string;
  Trvnr?: string;
  Land1Fr?: string;
  KostlT?: string;
  PersonNm?: string;
  Trvdat?: string;
  Trvtyp?: string;
  Urllink1?: string;
  Exp01Sum?: string;
  Exp02Sum?: string;
  Exp03Sum?: string;
  Exp04Sum?: string;
  Exp05Sum?: string;
  ZpaySum?: string;
  ZexpSum?: string;
  Exp01Ac?: string;
  Exp02Ac?: string;
  Exp03Ac?: string;
  Exp04Ac?: string;
  Exp05Ac?: string;
  Exp00Ac?: string;
  ZexpAcSum?: string;
  TravelItem?: { results?: SaTravelItem[] | null } | null;
  Zcount?: string;
  Trvtxt?: string;
  Remrk?: string;
  Kostl?: string;
  BudgetTotl?: string;
  ActualTotl?: string;
  Remain?: string;
  TravelItem2?: { results?: SaTravelPersonItem[] | null } | null;
};

export type SaTravelDraftSource = {
  code: number;
  attributes: unknown[];
  display: { source?: SaTravelHeader[] | null };
};

/** 177·179 공통 — 화면 구성·제목·저장 attributes 만 유형별 */
export type SaTravelExpenseState = {
  sapDocumentNo: string;
  zType: string;
  header: SaTravelHeader;
  /** SAP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교(입력 칸이 없어 자동 제목만으론 작성 내용 아님) */
  autoSubject: string;
};

/** 응답 배열의 첫 건만 사용(레거시 res.data[0]) */
export const pickSaTravelHeader = (
  source: SaTravelDraftSource | null | undefined,
): SaTravelHeader | null => {
  const first = source?.display?.source?.[0];
  return first && typeof first === 'object' ? first : null;
};

export const getSaTravelItems = (header: SaTravelHeader): SaTravelItem[] => {
  const results = header.TravelItem?.results;
  return Array.isArray(results) ? results : [];
};

/** 레거시 setData 의 null·undefined → '' */
export const saTravelText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const toNumber = (value: unknown): number => {
  const n = parseFloat(saTravelText(value).replace(/,/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

/**
 * 초과 사용률 — 레거시 그대로(사용자 확정).
 * 예산 0·실사용 있음 → 실사용 금액 + '%'. 일비만 덜 쓴 경우 '0.00%'(나머지는 '0%')
 */
export const formatSaTravelExceedRate = (
  budgetRaw: unknown,
  actualRaw: unknown,
  isDailyAllowance = false,
): string => {
  const budget = toNumber(budgetRaw);
  const actual = toNumber(actualRaw);
  const excess = actual - budget;
  const rate =
    budget !== 0 && actual !== 0 && (isDailyAllowance || excess >= 0)
      ? (excess / budget) * 100
      : 0;
  if (rate !== 0) return `${Math.max(rate, 0).toFixed(2)}%`;
  if (budget === 0 && actual !== 0) return `${actual.toLocaleString('ko-KR')}%`;
  return '0%';
};

/** 레거시 parseFloat(문구) > 0 → 빨간 글씨 */
export const isSaTravelRateExceeded = (rate: string): boolean =>
  parseFloat(rate) > 0;

export const SA_TRAVEL_EXPENSE_COLUMNS = [
  { label: '항공권', budget: 'Exp01Sum', actual: 'Exp01Ac' },
  { label: '숙박비', budget: 'Exp02Sum', actual: 'Exp02Ac' },
  { label: '비자', budget: 'Exp03Sum', actual: 'Exp03Ac' },
  { label: '보험', budget: 'Exp04Sum', actual: 'Exp04Ac' },
  { label: '기타', budget: 'Exp05Sum', actual: 'Exp05Ac' },
  { label: '일비', budget: 'ZpaySum', actual: 'Exp00Ac' },
] as const satisfies readonly {
  label: string;
  budget: keyof SaTravelHeader;
  actual: keyof SaTravelHeader;
}[];

const DAILY_ALLOWANCE_VOUCHER = 'K5';

const isDailyAllowanceRequest = (items: SaTravelItem[]): boolean =>
  items[0]?.Blart === DAILY_ALLOWANCE_VOUCHER;

/** 일비수령 여부 — K5 가 우선 */
export const getSaTravelDailyAllowanceNotice = (
  header: SaTravelHeader,
): string => {
  if (isDailyAllowanceRequest(getSaTravelItems(header)))
    return '일비 신청 건입니다.';
  return header.Zuse === 'Y' ? '일비가 지급된 건입니다.' : '';
};

/** 전기일에서 숫자만 남겨 YYYY-MM, 6자리 미만이면 '' */
const toYearMonth = (value: unknown): string => {
  const digits = saTravelText(value).replace(/\D/g, '');
  return digits.length >= 6
    ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}`
    : '';
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

// 제목은 HTML 이 아님 — 전역 escapeValue 그대로면 '&' 가 '&amp;' 로 저장됨
const NO_ESCAPE = { interpolation: { escapeValue: false } };

export const buildSaTravelExpenseSubject = (
  header: SaTravelHeader,
  t: Translate,
): string => {
  const items = getSaTravelItems(header);
  const code = saTravelText(header.Trvnr);
  const department = saTravelText(header.KostlT);
  if (items.length === 0)
    return t('해외출장비 비용 정산의 건({{department}})', {
      department,
      ...NO_ESCAPE,
    });
  if (isDailyAllowanceRequest(items))
    return t('해외출장 일비신청({{code}} / {{department}} / {{period}})', {
      code,
      department,
      period: saTravelText(header.Trvdat),
      ...NO_ESCAPE,
    });
  return t('해외출장 비용정산({{code}} / {{department}} / {{month}})', {
    code,
    department,
    month: toYearMonth(items[0].Budat),
    ...NO_ESCAPE,
  });
};

export const buildSaTravelRequestSubject = (
  header: SaTravelHeader,
  t: Translate,
): string =>
  t('해외출장 사전품의({{code}} / {{department}} / {{country}} / {{period}})', {
    code: saTravelText(header.Trvnr),
    department: saTravelText(header.KostlT),
    country: saTravelText(header.Land1Fr),
    period: saTravelText(header.Trvdat),
    ...NO_ESCAPE,
  });

export const buildSaTravelSubject = (
  apvType: number,
  header: SaTravelHeader,
  t: Translate,
): string =>
  isSaTravelRequestType(apvType)
    ? buildSaTravelRequestSubject(header, t)
    : buildSaTravelExpenseSubject(header, t);

export const getSaTravelPersonItems = (
  header: SaTravelHeader,
): SaTravelPersonItem[] | null => {
  const results = header.TravelItem2?.results;
  return Array.isArray(results) ? results : null;
};

export const SA_TRAVEL_REQUEST_COST_KEYS = [
  'Exp01',
  'Exp02',
  'Exp03',
  'Exp04',
  'Exp05',
  'Zpay',
] as const;

type SaTravelRequestCostKey = (typeof SA_TRAVEL_REQUEST_COST_KEYS)[number];

export type SaTravelRequestCost = {
  /** 사번(PernrNo)별 첫 행 + 인별 합계 */
  rows: { item: SaTravelPersonItem; total: string }[];
  sums: Record<SaTravelRequestCostKey, string>;
  total: string;
};

const legacyFloat = (value: unknown): number =>
  parseFloat((saTravelText(value) || '0').replace(/,/g, ''));

const legacyInt = (value: unknown): number => {
  const raw = saTravelText(value);
  return raw ? parseInt(raw.replace(/,/g, ''), 10) : 0;
};

/**
 * 출장비용 표 — 레거시 계산 그대로.
 * 인별 합계는 parseFloat, 맨 아래 합계 줄은 parseInt(소수 버림). TravelItem2 가 없으면 null(합계 줄 없음)
 */
export const getSaTravelRequestCost = (
  header: SaTravelHeader,
): SaTravelRequestCost | null => {
  const items = getSaTravelPersonItems(header);
  if (!items) return null;
  const seen = new Set<string>();
  const sums = Object.fromEntries(
    SA_TRAVEL_REQUEST_COST_KEYS.map((key) => [key, 0]),
  ) as Record<SaTravelRequestCostKey, number>;
  const rows: SaTravelRequestCost['rows'] = [];
  items.forEach((item) => {
    const pernrNo = item.PernrNo || '';
    if (seen.has(pernrNo)) return;
    seen.add(pernrNo);
    const total = SA_TRAVEL_REQUEST_COST_KEYS.reduce(
      (acc, key) => acc + legacyFloat(item[key]),
      0,
    );
    rows.push({ item, total: total.toLocaleString('ko-KR') });
    SA_TRAVEL_REQUEST_COST_KEYS.forEach((key) => {
      sums[key] += legacyInt(item[key]);
    });
  });
  const totalAll = SA_TRAVEL_REQUEST_COST_KEYS.reduce(
    (acc, key) => acc + sums[key],
    0,
  );
  return {
    rows,
    sums: Object.fromEntries(
      SA_TRAVEL_REQUEST_COST_KEYS.map((key) => [
        key,
        sums[key].toLocaleString('ko-KR'),
      ]),
    ) as Record<SaTravelRequestCostKey, string>,
    total: totalAll.toLocaleString('ko-KR'),
  };
};

export const SA_TRAVEL_MISSING_ENTRY_MESSAGE =
  '출장 신청 정보가 없습니다. SA 시스템에서 다시 들어와 주세요.';

export const SA_TRAVEL_EXPENSE_NOT_LOADED_MESSAGE =
  '데이터를 로드에 실패했습니다. 데이터 전표 확인 후 재상신해주세요';

export const checkSaTravelExpenseBeforeSave = (
  state: SaTravelExpenseState | null | undefined,
): string | null => (state ? null : SA_TRAVEL_EXPENSE_NOT_LOADED_MESSAGE);

/**
 * approvalLink 는 서버가 문서번호로 만들어 적재.
 * 179 는 SAP 원문(data)도 저장 — 최종 승인 메일 받는 사람(TravelItem2 PernrNo)을 여기서 찾음
 */
export const buildSaTravelExpenseAttributes = (
  state: SaTravelExpenseState | null | undefined,
  content: string,
  apvType: number = SA_TRAVEL_EXPENSE_TYPE,
): ApprovalAttributes | null => {
  if (!state) return null;
  const attributes = {
    sapDocumentNo: state.sapDocumentNo,
    zType: state.zType,
    content,
  };
  return isSaTravelRequestType(apvType)
    ? { ...attributes, data: state.header }
    : attributes;
};

export const parseSaTravelExpenseContent = (
  attributes: unknown,
): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};
